import { MenuItem, ObjectClass, ParseApp } from 'core';
import { LoggedUser, CustomFields, UIsObjects } from './entities';

enum CacheKeys {
  Settings = 'SETTINGS',
  LoggedUser = 'LOGGED_USER',
  MenuItems = 'MENU_ITEMS',
  ObjectClasses = 'OBJECT_CLASSES',
  CustomFields = 'CUSTOM_FIELDS',
  UIsObjects = 'UIS_OBJECTS',
  ParseApp = 'PARSE_APP'
}

function getItem<TItem>(key: CacheKeys): TItem | undefined {
  const itemString: string | null = localStorage.getItem(key);

  if (!itemString) {
    return undefined;
  }

  return JSON.parse(itemString) as TItem;
}

function safeStringify(item: unknown): string | undefined {
  try {
    return JSON.stringify(item);
  } catch {
    return undefined;
  }
}

function setItem(key: CacheKeys, item: unknown): void {
  const serialized = safeStringify(item);
  if (serialized === undefined) {
    return;
  }

  try {
    localStorage.setItem(key, serialized);
  } catch (error) {
    // Avoid crashing the app if we exceed localStorage quota; drop heavy keys instead.
    if (key === CacheKeys.UIsObjects) {
      localStorage.removeItem(CacheKeys.UIsObjects);
      // eslint-disable-next-line no-console
      console.warn('UIS_OBJECTS cache skipped because it exceeded storage quota', error);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Cache write failed for ${key}`, error);
    }
  }
}

export function getSettings(): { [key: string]: string } | undefined {
  return getItem<{ [key: string]: string }>(CacheKeys.Settings);
};

export function setSettings(settings: { [key: string]: string }): void {
  setItem(CacheKeys.Settings, settings);
};

export function getLoggedUser(): LoggedUser | undefined {
  return getItem<LoggedUser>(CacheKeys.LoggedUser);
};

export function setLoggedUser(loggedUser: LoggedUser): void {
  setItem(CacheKeys.LoggedUser, loggedUser);
};

export function clearLoggedUser(): void {
  localStorage.removeItem(CacheKeys.LoggedUser);
};

export function getMenuItems(): MenuItem[] | undefined {
  return getItem<MenuItem[]>(CacheKeys.MenuItems);
};

export function setMenuItems(menuItems: MenuItem[]): void {
  setItem(CacheKeys.MenuItems, menuItems);
};

export function clearMenuItems(): void {
  localStorage.removeItem(CacheKeys.MenuItems);
};

export function getObjectClasses(): ObjectClass[] | undefined {
  return getItem<ObjectClass[]>(CacheKeys.ObjectClasses);
};

export function setObjectClasses(objectClasses: ObjectClass[]): void {
  setItem(CacheKeys.ObjectClasses, objectClasses);
};

export function clearObjectClasses(): void {
  localStorage.removeItem(CacheKeys.ObjectClasses);
};

export function getCustomFields(): CustomFields | undefined {
  return getItem<CustomFields>(CacheKeys.CustomFields);
};

export function setCustomFields(customFields: CustomFields): void {
  setItem(CacheKeys.CustomFields, customFields);
};

export function clearCustomFields(): void {
  localStorage.removeItem(CacheKeys.CustomFields);
};

function shrinkUIsObjects(uisObjects: UIsObjects): UIsObjects {
  return Object.keys(uisObjects).reduce((acc: UIsObjects, uniqueId: string): UIsObjects => {
    const uiObjects = uisObjects[uniqueId];
    if (!uiObjects) {
      return acc;
    }

    acc[uniqueId] = {
      objectFilter: uiObjects.objectFilter,
      keepFiltered: uiObjects.keepFiltered,
      objectSort: uiObjects.objectSort,
      keepSorted: uiObjects.keepSorted,
      pageLimit: uiObjects.pageLimit,
      // Drop heavy data (objectsById/pagesIds) to stay within quota; they are rebuilt on reload.
      pagesIds: [[]],
      objectsById: {},
      fullTextSearchQueryValue: uiObjects.fullTextSearchQueryValue
    };

    return acc;
  }, {} as UIsObjects);
}

export function getUIsObjects(): UIsObjects | undefined {
  return getItem<UIsObjects>(CacheKeys.UIsObjects);
};

export function setUIsObjects(uisObjects: UIsObjects): void {
  setItem(CacheKeys.UIsObjects, shrinkUIsObjects(uisObjects));
};

export function clearUIsObjects(): void {
  localStorage.removeItem(CacheKeys.UIsObjects);
};

export function getParseApp(): ParseApp | undefined {
  return getItem<ParseApp>(CacheKeys.ParseApp);
}

export function setParseApp(parseApp: ParseApp): void {
  return setItem(CacheKeys.ParseApp, parseApp);
}
